import type React from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { Highlight } from 'app/types/commonTypes.type';
import { Badge } from '../../../ui/Badge';
import { ProblemCircleIcon } from '../../../ui/icons';
import { ModuleTrigger } from '../../../ui/ModuleTrigger';
import FullscreenOverlay from '../../../ui/FullscreenOverlay';
import { useDetailPanel } from '../../../ui/DetailPanel';
import { AmenitiesSpoke } from './AmenitiesSpoke';
import { AmenitiesSpokeContent } from './AmenitiesSpoke';
import { highlightsContainer, triggerContainer } from './amenitiesModule.styles';

const MAX_HIGHLIGHTS = 5;

const HIGHLIGHT_DISPLAY_OVERRIDES: Record<string, string> = {
  hvac: 'Air conditioning',
};

const getHighlightDisplay = (highlight: Highlight): string => {
  if (highlight.id === 'laundry' && highlight.types?.length > 0) {
    if (highlight.types.includes('In Unit')) return 'In-unit laundry';
    if (highlight.types.includes('Shared')) return 'Shared laundry';
    if (highlight.types.includes('No laundry')) return 'No laundry';
  }
  if (highlight.id === 'parking' && highlight.types?.length > 0) {
    if (highlight.types.includes('No Parking')) return 'No parking';
  }
  if (highlight.id === 'pets' && highlight.types?.length > 0) {
    if (highlight.types.includes('No Pets Allowed')) return 'No pets';
    const hasDogs = highlight.types.some((t) => /dog/i.test(t));
    const hasCats = highlight.types.some((t) => /cat/i.test(t));
    if (hasDogs && hasCats) return 'Pet-friendly';
    if (hasDogs) return 'Dog-friendly';
    if (hasCats) return 'Cat-friendly';
  }
  return HIGHLIGHT_DISPLAY_OVERRIDES[highlight.id] ?? highlight.display;
};

const isNegativeHighlight = (highlight: Highlight): boolean => {
  return getHighlightDisplay(highlight).startsWith('No ');
};

export const AmenitiesModule: React.FC = () => {
  const { openPanel, isMobile } = useDetailPanel();

  const amenitiesData = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing?.amenities);

  if (!amenitiesData?.highlights || amenitiesData.highlights.length === 0) {
    return null;
  }

  const EXCLUDED_HIGHLIGHT_IDS = ['appliances', 'outdoorAreas'];
  const visibleHighlights = amenitiesData.highlights
    .filter((h: Highlight) => !EXCLUDED_HIGHLIGHT_IDS.includes(h.id))
    .slice(0, MAX_HIGHLIGHTS);

  const renderTrigger = () => {
    if (!isMobile) {
      return (
        <div className={triggerContainer}>
          <ModuleTrigger aria-label="See all amenities" onClick={() => openPanel('amenities', AmenitiesSpokeContent)}>
            See all amenities
          </ModuleTrigger>
        </div>
      );
    }

    return (
      <div className={triggerContainer}>
        <FullscreenOverlay.Root defaultOpen={false}>
          <FullscreenOverlay.Trigger asChild>
            <ModuleTrigger aria-label="See all amenities">See all amenities</ModuleTrigger>
          </FullscreenOverlay.Trigger>

          <FullscreenOverlay.Portal>
            <FullscreenOverlay.Backdrop>
              <FullscreenOverlay.Content>
                <AmenitiesSpoke />
              </FullscreenOverlay.Content>
            </FullscreenOverlay.Backdrop>
          </FullscreenOverlay.Portal>
        </FullscreenOverlay.Root>
      </div>
    );
  };

  return (
    <>
      <div className={highlightsContainer}>
        {visibleHighlights.map((highlight) => {
          const isNegative = isNegativeHighlight(highlight);
          return (
            <Badge
              key={highlight.id}
              tone={isNegative ? 'alert' : 'primary'}
              size="sm"
              icon={isNegative ? <ProblemCircleIcon size={12} color="white" /> : undefined}
            >
              {getHighlightDisplay(highlight)}
            </Badge>
          );
        })}
      </div>
      {renderTrigger()}
    </>
  );
};
